import type { ThreadMessageDto } from '@aiai/shared';

/**
 * Opt-in browser notifications for PM posts in project threads. The setting is
 * per browser/device (localStorage, default off) and notifications only fire
 * while an aiai tab is open — they ride on the existing `/threads` socket.
 */
// Kept under the pre-threads key so an existing opt-in survives the rename.
const STORAGE_KEY = 'aiai.chatNotify';
const BODY_MAX = 140;

export function threadNotifySupported(): boolean {
  return typeof window !== 'undefined' && typeof window.Notification !== 'undefined';
}

export function threadNotifyPermission(): NotificationPermission | 'unsupported' {
  return threadNotifySupported() ? window.Notification.permission : 'unsupported';
}

export function isThreadNotifyEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setThreadNotifyEnabled(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode): the toggle just won't persist.
  }
}

/** Asks the browser for permission; resolves to the resulting state. */
export async function requestThreadNotifyPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!threadNotifySupported()) return 'unsupported';
  const current = window.Notification.permission;
  if (current !== 'default') return current;
  try {
    return await window.Notification.requestPermission();
  } catch {
    return window.Notification.permission;
  }
}

function truncate(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > BODY_MAX ? `${flat.slice(0, BODY_MAX - 1)}…` : flat;
}

/**
 * Shows a desktop notification for a new PM post, unless notifications are
 * off/unavailable, the post isn't from the PM, or the user is already looking
 * at that thread (tab visible and same thread). Returns whether a
 * notification was shown.
 */
export function maybeNotifyPmMessage(
  msg: ThreadMessageDto,
  opts: { activeThreadId: string | null; projectName?: string; href: string; onNavigate?: (href: string) => void },
): boolean {
  if (!isThreadNotifyEnabled() || !threadNotifySupported()) return false;
  if (window.Notification.permission !== 'granted') return false;
  if (msg.role !== 'pm') return false;
  const visible = document.visibilityState === 'visible';
  if (visible && msg.threadId === opts.activeThreadId) return false;

  const title = opts.projectName ? `PM replied · ${opts.projectName}` : 'PM replied';
  try {
    const n = new window.Notification(title, { body: truncate(msg.body), tag: msg.id });
    n.onclick = () => {
      window.focus();
      if (opts.onNavigate) opts.onNavigate(opts.href);
      else window.location.assign(opts.href);
      n.close();
    };
    return true;
  } catch {
    // Some browsers (Android Chrome) only allow notifications via a service worker.
    return false;
  }
}
